import type { NotificationItem } from '@hemmafixa/shared';
import { NotificationMenu } from '@hemmafixa/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { Area } from '@/app/navigation';
import { fill, useI18n } from '@/i18n/context';
import { useMarkRead, useNotifications, useUnreadCount } from './api';
import { useDescribeNotification } from './describe';

const MENU_SIZE = 6;

/** The top bar bell for every area: unread count, the latest notifications and a link to all of them. */
export function NotificationBell({ area }: { area: Area }) {
  const { t, relative } = useI18n();
  const n = t.notifications;
  const navigate = useNavigate();
  const describe = useDescribeNotification();
  const count = useUnreadCount().data?.count ?? 0;
  const [open, setOpen] = useState(false);
  // Loaded when the menu opens, so the top bar costs one small request a minute
  const latest = useNotifications({ pageSize: MENU_SIZE }, open);
  const markRead = useMarkRead();

  const select = (item: NotificationItem) => {
    if (!item.readAt) markRead.mutate({ ids: [item.id] });
    navigate(describe(item).href);
  };

  return (
    <NotificationMenu
      count={count}
      loading={latest.isLoading}
      onOpenChange={setOpen}
      items={(latest.data?.items ?? []).map((item) => {
        const view = describe(item);
        return {
          id: item.id,
          title: view.title,
          detail: view.detail,
          time: relative(item.createdAt),
          unread: !item.readAt,
          onSelect: () => select(item),
        };
      })}
      onMarkAllRead={() => markRead.mutate({ all: true })}
      onViewAll={() => navigate(`/${area}/notifications`)}
      labels={{
        button: count > 0 ? fill(t.layout.notificationsUnread, { count }) : n.title,
        title: n.title,
        markAllRead: n.markAllRead,
        viewAll: n.viewAll,
        empty: n.empty,
        unread: n.unread,
      }}
    />
  );
}
